/**
 * VoiceRecorder
 *
 * In-line voice recording UI that replaces the chat input.
 * Uses MediaRecorder API to capture audio.
 */

import { useState, useRef, useEffect } from 'react';
import {
  MicrophoneIcon,
  StopIcon,
  XMarkIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/solid';

export default function VoiceRecorder({ onSend, onCancel }) {
  const [recording, setRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    startRecording();
    return () => {
      stopRecording();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm'
      });

      chunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setRecording(true);
      startTimeRef.current = Date.now();

      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } catch (err) {
      console.error('Microphone access denied:', err);
      onCancel?.();
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecording(false);
  };

  const handleSend = () => {
    if (audioBlob) {
      onSend?.(audioBlob, duration);
    }
  };

  const handleCancel = () => {
    stopRecording();
    onCancel?.();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="border-t border-base-300 bg-base-100 p-3">
      <div className="flex items-center gap-3">
        {/* Cancel */}
        <button className="btn btn-ghost btn-sm btn-circle" onClick={handleCancel}>
          <XMarkIcon className="w-4 h-4" />
        </button>

        {/* Recording indicator */}
        <div className="flex-1 flex items-center gap-3">
          {recording && (
            <span className="w-2 h-2 bg-error rounded-full animate-pulse" />
          )}
          <span className="text-sm font-mono">{formatTime(duration)}</span>
          {recording && (
            <span className="text-xs text-base-content/60">Recording...</span>
          )}
        </div>

        {/* Stop / Send */}
        {recording ? (
          <button className="btn btn-error btn-sm btn-circle" onClick={stopRecording}>
            <StopIcon className="w-4 h-4" />
          </button>
        ) : audioBlob ? (
          <button className="btn btn-primary btn-sm" onClick={handleSend}>
            <PaperAirplaneIcon className="w-4 h-4" />
            Send
          </button>
        ) : null}
      </div>
    </div>
  );
}
